import { useMemo } from "react";

import type { Result } from "@tsmono/inspect-common/types";

import { buildSearchScope } from "./searchRequest";
import type { SearchPanelApi, SearchScope } from "./types";
import { useCachedSearchResult } from "./useSearchQueries";

export type SearchReferenceLabels = {
  messageLabels?: Record<string, string>;
  eventLabels?: Record<string, string>;
};

/**
 * Projects a search result's citations onto transcript labels: `message`
 * references become `messageLabels`, `event` references become `eventLabels`,
 * both keyed by reference id. Returns undefined when nothing carries a cite so
 * callers can treat "no labels" as a single absence.
 */
export const deriveSearchReferenceLabels = (
  result: Result | null | undefined
): SearchReferenceLabels | undefined => {
  const messageLabels: Record<string, string> = {};
  const eventLabels: Record<string, string> = {};
  for (const ref of result?.references ?? []) {
    if (!ref.cite) continue;
    if (ref.type === "message") {
      messageLabels[ref.id] = ref.cite;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (ref.type === "event") {
      eventLabels[ref.id] = ref.cite;
    }
  }

  const hasMessageLabels = Object.keys(messageLabels).length > 0;
  const hasEventLabels = Object.keys(eventLabels).length > 0;
  if (!hasMessageLabels && !hasEventLabels) return undefined;

  return {
    ...(hasMessageLabels ? { messageLabels } : {}),
    ...(hasEventLabels ? { eventLabels } : {}),
  };
};

/**
 * Reads the cached result for an active search and derives transcript labels
 * from its citations. Host apps resolve `searchId` from their own store and
 * supply the matching `api`; passing a null api or searchId yields undefined.
 */
export const useCachedSearchReferenceLabels = ({
  api,
  scope,
  searchId,
}: {
  api: SearchPanelApi | null;
  scope: SearchScope;
  searchId: string | null;
}): SearchReferenceLabels | undefined => {
  const cachedResult = useCachedSearchResult({
    api,
    scope: buildSearchScope(scope),
    searchId: api ? searchId : null,
  });

  return useMemo(
    () => deriveSearchReferenceLabels(cachedResult.data),
    [cachedResult.data]
  );
};
